import { isOwnedStation } from '@app/core/stations/helpers';
import { LinkItem } from '@mi-front/ui';
import { useIntl } from 'react-intl';
import { useIsContentAiForStationEnabled } from '../../../../content-ai/hooks/useIsContentAiForStationEnabled';
import { mediaNotReadyTooltip, transcriptionNotReadyTooltip } from '../fragment-view-tab-messages';
import { useFragment } from '../hooks/useFragment';
import { Station } from '@app/core/types/stations';

/**
 * Returns the tab items for the fragment view header (Info, Archivos, edits, Publicar, etc.).
 * Tabs are filtered by station type and feature flags (e.g. Content AI).
 * Expects {@link useFragmentCtx} to be available (fragment view page).
 */
export function useFragmentTabs(station: Station): LinkItem[] {
  const { fragment } = useFragment();
  const { $t } = useIntl();

  const isContentAiEnabled = useIsContentAiForStationEnabled(station.id);
  const isMediaReady = !!fragment.mediaCompletedAt;
  const isTranscriptionReady = fragment.transcriptionCompletedAt !== null;
  const canPublish = isOwnedStation(station);

  const tabs: (LinkItem | null)[] = [
    {
      exact: true,
      to: `/fragments/${fragment.id}`,
      text: $t({
        id: 'fragments.viewScreen.title',
        defaultMessage: 'Info',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}`,
    },
    {
      to: `/fragments/${fragment.id}/files`,
      text: $t({
        id: 'fragments.exportView.tabFiles',
        defaultMessage: 'Archivos',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/files`,
      disabled: !isMediaReady,
      tooltip: !isMediaReady ? $t(mediaNotReadyTooltip) : undefined,
    },
    {
      to: `/fragments/${fragment.id}/edits`,
      text: $t({
        id: 'videoEditions.tabTitle',
        defaultMessage: 'edits',
      }),
      disabled: !isTranscriptionReady,
      tooltip: !isTranscriptionReady ? $t(transcriptionNotReadyTooltip) : undefined,
    },
    canPublish
      ? {
          to: `/fragments/${fragment.id}/publish`,
          text: $t({
            id: 'fragments.exportView.tabPublish',
            defaultMessage: 'Publicar',
          }),
          isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/publish`,
          disabled: !isMediaReady,
          tooltip: !isMediaReady ? $t(mediaNotReadyTooltip) : undefined,
        }
      : null,
    isContentAiEnabled
      ? ({
          to: `/fragments/${fragment.id}/content-ai`,
          text: $t({
            id: 'contentAi.title',
            defaultMessage: 'Content AI',
          }),
          disabled: !isTranscriptionReady,
          tooltip: !isTranscriptionReady ? $t(transcriptionNotReadyTooltip) : undefined,
        } as LinkItem)
      : null,
  ];

  return tabs.filter((v): v is LinkItem => Boolean(v));
}
